import { useEffect, useMemo, useState } from 'react'
import { formatOdds } from '@feed-widget/utils/odds'
import { selectionPresentation } from '@feed-widget/utils/selectionSet'
import { Archive, ArchiveRestore, Edit3, RefreshCw, Trash2 } from 'lucide-react'
import BrandedFeedPreview from './BrandedFeedPreview'
import { Button, ModalShell, StatusBadge } from './common'
import CodeBlock from './common/data/CodeBlock'
import { getPreviewSelections, normalizeFeedPreviewItem } from '../utils/feedPreviewItem'
import { labelForDisplayType } from '../constants/displayTypes'
import styles from './PostPreviewModal.module.css'

const UNDERSCORE_PATTERN = /_/g

const LIFECYCLE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function lifecycleDate(value) {
  if (!value) return 'Never'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unavailable' : LIFECYCLE_DATE_FORMATTER.format(date)
}


export default function PostPreviewModal({
  open,
  post,
  branding,
  pendingAction,
  onClose,
  onEdit,
  onDelete,
  onUndoPendingDelete,
  onForceDelete,
  canForceDelete = false,
  onToggleHidden,
  visibilityPending = false,
  onReprocess,
  canViewRawData = false,
}) {
  const surfaces = useMemo(
    () => (Array.isArray(post?.surface_targets) ? post.surface_targets : []),
    [post?.surface_targets],
  )
  const [activeSurfaceId, setActiveSurfaceId] = useState(null)
  const [rawDataOpen, setRawDataOpen] = useState(false)

  useEffect(() => {
    if (!open || surfaces.length === 0) {
      setActiveSurfaceId(null)
      return
    }
    if (!surfaces.some((surface) => surface.id === activeSurfaceId || surface.slug === activeSurfaceId)) {
      setActiveSurfaceId(surfaces[0].id || surfaces[0].slug)
    }
  }, [activeSurfaceId, open, surfaces])

  useEffect(() => {
    if (!open) setRawDataOpen(false)
  }, [open, post?.id])

  const activeSurface = useMemo(
    () => surfaces.find((surface) => surface.id === activeSurfaceId || surface.slug === activeSurfaceId) || surfaces[0] || null,
    [activeSurfaceId, surfaces],
  )
  const previewItem = useMemo(() => normalizeFeedPreviewItem(post), [post])
  const legPresentations = useMemo(
    () => getPreviewSelections(previewItem).map(selectionPresentation),
    [previewItem],
  )
  const creatorEmail = post?.posted_by_email || null
  const rawPreviewData = useMemo(() => ({
    feed_payload: post?.feed_payload || null,
    bet_legs: post?.bet_legs || [],
    selection_set: previewItem?.selection_set || null,
  }), [post, previewItem])

  if (!post) return null

  const statusType =
    post.approval_status === 'approved' ? 'info' :
    post.approval_status === 'pending_approval' ? 'warning' :
    post.approval_status === 'rejected' ? 'error' :
    'default'

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Post preview"
      description={post.creator?.name ? `Created by ${post.creator.name}` : undefined}
      size="full"
      contentClassName={styles.content}
      footer={
        <>
          {onEdit ? (
            <Button variant="secondary" onClick={() => onEdit(post.id)}>
              <Edit3 size={16} />
              Edit
            </Button>
          ) : null}
          {pendingAction && post.approval_status === 'pending_approval' ? (
            <Button onClick={pendingAction.onClick} disabled={pendingAction.disabled}>
              {pendingAction.label}
            </Button>
          ) : null}
          {post.approval_status === 'approved' && onReprocess ? (
            <Button variant="secondary" onClick={() => onReprocess(post.id)}>
              <RefreshCw size={16} />
              Rebuild published post
            </Button>
          ) : null}
          {onToggleHidden ? (
            <Button
              variant="secondary"
              onClick={() => onToggleHidden(post.id, !post.is_hidden)}
              disabled={visibilityPending}
            >
              {post.is_hidden ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              {post.is_hidden ? 'Restore post' : 'Archive post'}
            </Button>
          ) : null}
          {onDelete && post.lifecycle !== 'pending_delete' ? (
            <Button
              variant="danger"
              onClick={() => onDelete(post.id)}
              disabled={post.delete_capability?.allowed !== true}
              title={post.delete_capability?.message}
            >
              <Trash2 size={16} />
              Delete
            </Button>
          ) : null}
          {onUndoPendingDelete && post.lifecycle === 'pending_delete' ? (
            <Button variant="secondary" onClick={() => onUndoPendingDelete(post.id)}>
              <ArchiveRestore size={16} />
              Undo deletion
            </Button>
          ) : null}
          {onForceDelete && canForceDelete && post.lifecycle === 'pending_delete' ? (
            <Button variant="danger" onClick={() => onForceDelete(post.id)}>
              <Trash2 size={16} />
              Erase now
            </Button>
          ) : null}
        </>
      }
    >
      <div className={styles.surfaceTabs}>
        {surfaces.length > 0 ? (
          surfaces.map((surface) => {
            const id = surface.id || surface.slug
            return (
              <button
                key={id}
                type="button"
                className={`${styles.surfaceTab} ${id === activeSurfaceId ? styles.surfaceTabActive : ''}`}
                onClick={() => setActiveSurfaceId(id)}
              >
                <span>{surface.name || surface.slug}</span>
                {surface.display_type ? <small>{labelForDisplayType(surface.display_type)}</small> : null}
              </button>
            )
          })
        ) : (
          <span className={styles.unpublishedState}>Not published to a surface</span>
        )}
      </div>

      <div className={styles.workspace}>
        <div className={styles.previewColumn}>
          <BrandedFeedPreview
            branding={branding}
            item={previewItem}
            title={activeSurface ? activeSurface.name || activeSurface.slug : 'Post surface'}
            eyebrow="Live surface preview"
            displayType={activeSurface?.display_type}
            surface={activeSurface}
            allowFullscreen
            testId="post-preview-feed"
          />
        </div>

        <aside className={styles.detailColumn}>
          <div className={styles.statusRow}>
            <StatusBadge type={statusType}>{(post.approval_status || '').replace(UNDERSCORE_PATTERN, ' ')}</StatusBadge>
            {post.post_type ? <span className={styles.metaPill}>{post.post_type.replace(UNDERSCORE_PATTERN, ' ')}</span> : null}
            {post.is_pinned ? <span className={styles.metaPill}>Pinned</span> : null}
            {post.is_hidden ? <span className={styles.metaPill}>Archived</span> : null}
          </div>


          <section className={styles.detailSection}>
            <h3>Copy</h3>
            <p className={styles.postCopy}>{post.text || 'No post copy.'}</p>
          </section>

          <section className={styles.detailSection}>
            <h3>Lifecycle</h3>
            <dl className={styles.lifecycleList}>
              <div>
                <dt>First published</dt>
                <dd>{lifecycleDate(post.first_published_at)}</dd>
              </div>
              {post.is_hidden ? (
                <>
                  <div>
                    <dt>Archived</dt>
                    <dd>{lifecycleDate(post.operator_hidden_at)}</dd>
                  </div>
                  <div>
                    <dt>Archived by</dt>
                    <dd>{post.operator_hidden_by_email || 'Unknown operator'}</dd>
                  </div>
                  <div>
                    <dt>Reason</dt>
                    <dd>{post.operator_hidden_reason || 'No reason recorded'}</dd>
                  </div>
                </>
              ) : null}
            </dl>
            {post.delete_capability?.message ? (
              <p className={styles.lifecyclePolicy}>{post.delete_capability.message}</p>
            ) : null}
          </section>

          {legPresentations.length > 0 ? (
            <section className={styles.detailSection}>
              <h3>Bet legs</h3>
              <div className={styles.legList}>
                {legPresentations.map((leg, index) => (
                  <div className={styles.legDetail} key={`${leg.outcomeId || 'leg'}-${index}`}>
                    <strong>{leg.selection || 'Selection'}</strong>
                    <span>{[leg.event, leg.market, formatOdds(leg.odds)].filter(Boolean).join(' · ')}</span>
                    <dl>
                      <div><dt>Outcome ID</dt><dd>{leg.outcomeId || 'Unavailable'}</dd></div>
                      <div><dt>Bet offer ID</dt><dd>{leg.betOfferId || 'Unavailable'}</dd></div>
                    </dl>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {post.tags?.length ? (
            <section className={styles.detailSection}>
              <h3>Tags</h3>
              <div className={styles.tagRow}>
                {post.tags.map((tag, index) => (
                  <span
                    key={tag.id || index}
                    className={styles.tag}
                    style={{ backgroundColor: tag.bg_color, color: tag.text_color }}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </section>
          ) : null}


          {post.cta?.kind && post.cta.kind !== 'none' && post.cta.kind !== 'betslip' ? (
            <section className={styles.detailSection}>
              <h3>CTA</h3>
              <div className={styles.ctaPreview}>
                <span>{post.cta.label || post.cta.kind}</span>
                {post.cta.target ? <code>{post.cta.target}</code> : null}
              </div>
            </section>
          ) : null}

          {canViewRawData ? (
            <section className={styles.detailSection}>
              <div className={styles.detailSectionHeader}>
                <h3>Raw data</h3>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setRawDataOpen((value) => !value)}
                >
                  {rawDataOpen ? 'Hide raw data' : 'Show raw data'}
                </Button>
              </div>
              {rawDataOpen ? (
                <div data-testid="post-preview-raw-data">
                  <CodeBlock value={rawPreviewData} label="Raw post data" language="json" />
                </div>
              ) : null}
            </section>
          ) : null}

          <section className={styles.detailSection}>
            <h3>Feed controls</h3>
            <div className={styles.metaGrid}>
              <span>System score</span>
              <strong>{(post.score || 1).toFixed(1)}</strong>
              <span>Algorithm boost</span>
              <strong>{post.boost_factor || 1}</strong>
              <span>Created</span>
              <strong>{post.created_at ? new Date(post.created_at).toLocaleString() : '--'}</strong>
              <span>Creator email</span>
              <strong>{creatorEmail || '--'}</strong>
            </div>
          </section>
        </aside>
      </div>
    </ModalShell>
  )
}
